import React from "react";
import { PlusIcon } from "@heroicons/react/24/outline";
import { Tooltip } from "@src/common/components/Tooltip";
import {
  PopoverTrigger,
  PopoverContent,
  PopoverHeading,
  PopoverDescription,
  Popover,
} from "@src/common/components";
import CourieButton from "@src/common/components/Button";
import { LabelAssignmentForm } from "./LabelAssignmentForm";
import {
  Label,
  LabelEntityType,
  useGetLabelsQuery,
  useUnassignLabelsFromEntityMutation,
  useAssignLabelsToEntitiesMutation,
} from "@api/graphql/generated/generated-types";
import { useErrorToast } from "@src/common/hooks/useErrorToast";
import { useAuth } from "@src/common/hooks/useAuth";

interface LabelAssignmentPopoverProps {
  entityId: string;
  selectedLabels: Label[];
  isPopoverOpen: boolean;
  onOpenPopover: () => void;
  onClosePopover: () => void;
  onLabelCreated?: () => void | undefined;
  onLabelAssignmentChanged: (res: any) => void;
  entityType: LabelEntityType;
  labelsData?: Label[];
  labelsLoading?: boolean;
  labelsError?: any;
  zIndex?: number;
}

export function LabelAssignmentPopover({
  selectedLabels,
  isPopoverOpen,
  onOpenPopover,
  onClosePopover,
  onLabelCreated = undefined,
  onLabelAssignmentChanged,
  entityId,
  entityType,
  labelsData: externalLabelsData,
  labelsLoading: externalLabelsLoading,
  labelsError: externalLabelsError,
  zIndex,
}: LabelAssignmentPopoverProps) {
  const { courier } = useAuth();
  const courierId = courier?.courier?.id;
  const showError = useErrorToast();

  // Labels fetching - use external data if provided, otherwise fetch internally
  const {
    data: internalLabelsData,
    loading: internalLabelsLoading,
    error: internalLabelsError,
    refetch: refetchLabels,
  } = useGetLabelsQuery({
    variables: {
      courierId: courierId || "",
      entityType: entityType,
    },
    skip: !courierId || externalLabelsData !== undefined,
    fetchPolicy: "network-only",
    onError: (error) => {
      showError(error);
    },
  });

  // Use external data if provided, otherwise use internal data
  const labelsData = externalLabelsData || internalLabelsData?.labels;
  const labelsLoading =
    externalLabelsLoading !== undefined
      ? externalLabelsLoading
      : internalLabelsLoading;
  const labelsError = externalLabelsError || internalLabelsError;

  // Unassign labels mutation
  const [unassignLabelsFromEntityMutation, { loading: isUnassigning }] =
    useUnassignLabelsFromEntityMutation({
      onError: (error) => {
        showError(error);
      },
      onCompleted: (res) => {
        onLabelAssignmentChanged(res);
        onClosePopover();
      },
    });

  // Assign labels mutation
  const [assignLabelsToEntitiesMutation, { loading: isAssigningMutation }] =
    useAssignLabelsToEntitiesMutation({
      onError: (error) => {
        showError(error);
      },
      onCompleted: (res) => {
        onLabelAssignmentChanged(res);
        onClosePopover();
      },
    });

  const labels = labelsData || [];

  const handleAssignLabel = (labelId: string) => {
    if (!courierId) return;

    assignLabelsToEntitiesMutation({
      variables: {
        courierId,
        entityType: entityType,
        labelIds: [labelId],
        entityIds: [entityId],
      },
    });
  };

  const handleUnassignLabel = (labelId: string) => {
    if (!courierId) return;

    unassignLabelsFromEntityMutation({
      variables: {
        courierId,
        entityType: entityType,
        labelIds: [labelId],
        entityId: entityId,
      },
    });
  };

  return (
    <Popover open={isPopoverOpen} onOpenChange={onClosePopover}>
      <PopoverTrigger>
        <CourieButton
          onClick={onOpenPopover}
          color="gray"
          size="xs"
          disabled={false}
        >
          <Tooltip content="Assign Labels">
            <PlusIcon className="w-4 h-4" />
          </Tooltip>
        </CourieButton>
      </PopoverTrigger>
      <PopoverContent
        style={{
          width: 280,
          minWidth: 280,
          maxWidth: 280,
          zIndex: zIndex || 30,
        }}
        className="absolute bg-white dark:bg-gray-800 border dark:border-gray-700 p-2 rounded-md shadow-2xl"
      >
        <PopoverHeading className="mb-1 text-sm font-semibold text-slate-700 dark:text-gray-200">
          Assign Labels
        </PopoverHeading>
        <PopoverDescription>
          <LabelAssignmentForm
            labelsData={labels}
            labelsLoading={labelsLoading}
            labelsError={labelsError}
            selectedLabels={selectedLabels}
            onAssignLabel={handleAssignLabel}
            onUnassignLabel={handleUnassignLabel}
            isAssigning={isAssigningMutation}
            isUnassigning={isUnassigning}
            onLabelCreated={() => {
              if (refetchLabels) {
                refetchLabels();
              } else if (onLabelCreated) {
                onLabelCreated();
              }
            }}
            entityType={entityType}
          />
        </PopoverDescription>
      </PopoverContent>
    </Popover>
  );
}
